import React from 'react';
import { graphql } from 'gatsby';
import Container from '../../primitives/grid/container';
import Image from '../../primitives/image';
import { useMicrosite } from '../../../contexts/franchise/microsite-context';
import DoubleArrowIcon from '../../../images/svg/double-arrow.svg';
import TripleArrowIcon from '../../../images/svg/triple-arrow.svg';
import { colors } from '../../primitives/colors';
import Col from '../../primitives/grid/col';
import Row from '../../primitives/grid/row';
import Link from '../../primitives/link';
import NotchedSlider from '../../primitives/notched-slider';
import { breakpoints } from '../../primitives/tokens';
import Typography from '../../primitives/typography';

const IconButtonsSlider: React.FC<Queries.IconButtonsSliderDataFragment> = (data) => {
    const { isMicrosite } = useMicrosite();
    const { slides } = data;

    if (!slides || slides.length === 0) return null;

    return (
        <Container>
            <div css={{
                display: 'none',
                [breakpoints.tb]: {
                    display: 'block',
                    padding: '32px 0',
                },
            }}
            >
                <Row>
                    {slides.map((slide) => {
                        if (!slide) return null;
                        const { label, link, icon } = slide;
                        if (!link?.href) return null;
                        return (
                            <Col
                                key={`${label}-${link.href}`}
                                breakpoints={{ dt: { span: 6 }, tb: { span: 3 }, mb: { span: 2 } }}
                                css={{
                                    position: 'relative',
                                    '&:after': {
                                        content: '""',
                                        position: 'absolute',
                                        top: '8px',
                                        height: '100px',
                                        backgroundColor: colors.grey200,
                                        right: '-12px',
                                        width: '1px',
                                    },
                                    '&:nth-of-type(4n):after': {
                                        display: 'none',
                                    },
                                    [breakpoints.mb]: {
                                        '&:after': {
                                            right: '-8px',
                                        },
                                        '&:nth-of-type(2n):after': {
                                            display: 'none',
                                        },
                                    },
                                }}
                            >
                                <Link
                                    to={link.href}
                                    css={{
                                        display: 'block',
                                        padding: '16px 0',
                                        [breakpoints.mb]: {
                                            padding: '8px 0',
                                        },
                                    }}
                                >
                                    <div css={{ height: '100%' }}>
                                        {icon?.url && (
                                            <div css={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                margin: '0 auto',
                                                marginBottom: '12px',
                                            }}
                                            >
                                                <Image
                                                    css={{
                                                        height: '64px',
                                                        width: '64px',
                                                        objectFit: 'contain',
                                                        objectPosition: 'center',
                                                    }}
                                                    image={icon}
                                                    alt={icon.title || ''}
                                                />
                                            </div>
                                        )}
                                        <Typography fontWeight="medium" css={{ textAlign: 'center', marginBottom: '12px' }}>
                                            {label}
                                        </Typography>
                                        <div
                                            css={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                opacity: 0,
                                                transition: '0.2s',
                                                '[data-active="true"] &': {
                                                    opacity: 1,
                                                },
                                            }}
                                        >

                                            <TripleArrowIcon />
                                        </div>
                                    </div>
                                </Link>
                            </Col>
                        );
                    })}
                </Row>
            </div>
            <div css={{
                paddingBottom: '100px',
                [breakpoints.tb]: {
                    display: 'none',
                },
            }}
            >
                <NotchedSlider>
                    <Row>

                        <Col breakpoints={{
                            dt: { span: 1 },
                        }}
                        >
                            {slides.length > 3 && (
                                <div css={{
                                    display: 'flex',
                                    height: '100%',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                >
                                    <NotchedSlider.SliderLeftButton>
                                        <div
                                            css={{
                                                display: 'flex',
                                                position: 'relative',
                                                marginBottom: '40px',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                fontSize: '12px',
                                                lineHeight: '16px',
                                                color: '#9CA3AF',
                                                ...(isMicrosite ? {
                                                    svg: {
                                                        transition: 'all .1s',
                                                    },
                                                    '&:hover': {
                                                        svg: {
                                                            transition: 'all .15s',
                                                            transform: 'scale(1.22)',
                                                        },
                                                    },
                                                } : {}),
                                            }}
                                        >
                                            <DoubleArrowIcon css={{
                                                marginTop: '12px',
                                            }}
                                            />
                                            <div css={{
                                                marginTop: '8px',
                                                position: 'absolute',
                                                bottom: '-32px',
                                                whiteSpace: 'nowrap',
                                            }}
                                            >
                                                Previous
                                            </div>
                                        </div>
                                    </NotchedSlider.SliderLeftButton>
                                </div>
                            )}
                        </Col>

                        <Col
                            breakpoints={{
                                dt: { span: 10 },
                            }}
                            css={{ position: 'relative', zIndex: 2 }}
                        >
                            <div css={{
                                position: 'absolute',
                                display: 'flex',
                                justifyContent: 'center',
                                pointerEvents: 'none',
                                width: '100%',
                            }}
                            >
                                <div css={{
                                    height: '190px',
                                    boxShadow: '0px 0px 9px 0 rgba(0, 0, 0, 0.16)',
                                    position: 'relative',
                                    width: '20%',
                                    '&:before': {
                                        clipPath: 'polygon(0% 30%, 100% 30%, 50% 100%)',
                                        height: '58px',
                                        content: '""',
                                        display: 'block',
                                        position: 'absolute',
                                        bottom: '-40px',
                                        backgroundColor: colors.shadesWhite,
                                        zIndex: 1,
                                        width: '100%',
                                    },
                                    '&:after': {
                                        aspectRatio: '1',
                                        clipPath: 'polygon(0% 76%, 50% 95%, 100% 76%, 100% 79%, 50% 98%, 0% 79%)',
                                        bottom: '-46px',
                                        content: '""',
                                        display: 'block',
                                        position: 'absolute',
                                        width: '100%',
                                        height: '100%',
                                        top: 'auto',
                                        zIndex: 1,
                                        backgroundColor: colors.primaryOrange,
                                    },
                                }}
                                />
                            </div>
                            {slides.length > 3 && (
                                <NotchedSlider.Slides centered slidesPerView={5}>
                                    {slides.map((slide) => {
                                        if (!slide) return null;
                                        const { label, link, icon } = slide;
                                        if (!link?.href) return null;
                                        return (
                                            <div
                                                key={`${label}-${link.href}`}
                                                css={{
                                                    paddingTop: '12px',
                                                    paddingBottom: '4px',
                                                }}
                                            >
                                                <Link
                                                    to={link.href}
                                                    css={{
                                                        display: 'block',
                                                        paddingTop: '48px',
                                                        paddingBottom: '16px',
                                                        transition: '0.2s',
                                                        '[data-active="true"] &': {
                                                            transform: 'translateY(-7px)',
                                                        },
                                                    }}
                                                >
                                                    <div css={{ height: '100%' }}>
                                                        {icon?.url && (
                                                            <div css={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                margin: '0 auto',
                                                                marginBottom: '12px',
                                                            }}
                                                            >
                                                                <Image
                                                                    css={{
                                                                        height: '64px',
                                                                        width: '64px',
                                                                        objectFit: 'contain',
                                                                        objectPosition: 'center',
                                                                    }}
                                                                    image={icon}
                                                                    alt={icon.title || ''}
                                                                />
                                                            </div>
                                                        )}
                                                        <Typography fontWeight="medium" css={{ textAlign: 'center', marginBottom: '12px' }}>
                                                            {label}
                                                        </Typography>
                                                        <div
                                                            css={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                opacity: 0,
                                                                transition: '0.2s',
                                                                '[data-active="true"] &': {
                                                                    opacity: 1,
                                                                },
                                                            }}
                                                        >
                                                            <TripleArrowIcon />
                                                        </div>
                                                    </div>
                                                </Link>
                                            </div>
                                        );
                                    })}
                                </NotchedSlider.Slides>
                            )}
                            {slides.length <= 3 && (
                                <div css={{ display: 'flex', justifyContent: 'center' }}>
                                    {slides.map((slide) => {
                                        if (!slide) return null;
                                        const { label, link, icon } = slide;
                                        if (!link?.href) return null;
                                        return (
                                            <div css={{
                                                paddingTop: '12px',
                                                paddingBottom: '4px',
                                                minWidth: '20%',
                                                maxWidth: '20%',
                                            }}
                                            >
                                                <Link
                                                    to={link.href}
                                                    css={{
                                                        display: 'block',
                                                        paddingTop: '48px',
                                                        paddingBottom: '16px',
                                                        transition: '0.2s',
                                                    }}
                                                >
                                                    <div css={{ height: '100%' }} key={label}>
                                                        {icon?.url && (
                                                            <div css={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                margin: '0 auto',
                                                                marginBottom: '12px',
                                                            }}
                                                            >
                                                                <Image
                                                                    css={{
                                                                        height: '64px',
                                                                        width: '64px',
                                                                        objectFit: 'contain',
                                                                        objectPosition: 'center',
                                                                    }}
                                                                    image={icon}
                                                                    alt={icon.title || ''}
                                                                />
                                                            </div>
                                                        )}
                                                        <Typography fontWeight="medium" css={{ textAlign: 'center', marginBottom: '12px' }}>
                                                            {label}
                                                        </Typography>
                                                    </div>
                                                </Link>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Col>
                        <Col breakpoints={{
                            dt: { span: 1 },
                        }}
                        >
                            {slides.length > 3 && (
                                <div css={{
                                    display: 'flex',
                                    height: '100%',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                >
                                    <NotchedSlider.SliderRightButton>
                                        <div
                                            css={{
                                                display: 'flex',
                                                position: 'relative',
                                                marginBottom: '40px',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                fontSize: '12px',
                                                lineHeight: '16px',
                                                color: '#9CA3AF',
                                                ...(isMicrosite ? {
                                                    svg: {
                                                        transition: 'all .1s',
                                                    },
                                                    '&:hover': {
                                                        svg: {
                                                            transition: 'all .15s',
                                                            transform: 'scale(1.22) rotate(180deg)',
                                                        },
                                                    },
                                                } : {}),
                                            }}
                                        >
                                            <DoubleArrowIcon css={{
                                                marginTop: '12px',
                                                transform: 'rotate(180deg)',
                                            }}
                                            />
                                            <div css={{
                                                marginTop: '8px',
                                                position: 'absolute',
                                                bottom: '-32px',
                                                whiteSpace: 'nowrap',
                                            }}
                                            >
                                                Next
                                            </div>
                                        </div>
                                    </NotchedSlider.SliderRightButton>
                                </div>
                            )}
                        </Col>

                    </Row>
                </NotchedSlider>
            </div>
        </Container>
    );
};

export default IconButtonsSlider;

export const query = graphql`
    fragment IconButtonsSliderData on CsIconButtonsGroup {
        slides {
            label
            icon {
                filename
                title
                url
                contentStackAsset(width: 100)
            }
            link {
                href
                title
            }
        }
    }
`;
